import React, { useState } from 'react';
import {
  Box,
  FormControl,
  FormLabel,
  FormErrorMessage,
  FormHelperText,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Textarea,
  Select,
  Button,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  VStack,
  HStack,
  Text,
  Icon,
  IconButton,
  useToast,
} from '@chakra-ui/react';
import { FaEye, FaEyeSlash, FaDollarSign } from 'react-icons/fa';

// Form Field Wrapper with consistent styling
interface FormFieldProps {
  label?: string;
  helperText?: string;
  errorMessage?: string;
  isRequired?: boolean;
  isInvalid?: boolean;
  children: React.ReactNode;
}

export const FormField: React.FC<FormFieldProps> = ({
  label,
  helperText,
  errorMessage,
  isRequired = false,
  isInvalid = false,
  children,
}) => {
  return (
    <FormControl isRequired={isRequired} isInvalid={isInvalid} mb={4}>
      {label && (
        <FormLabel color="brand.green.dark" fontWeight="semibold" fontSize="sm">
          {label}
        </FormLabel>
      )}
      {children}
      {errorMessage && (
        <FormErrorMessage fontSize="sm">{errorMessage}</FormErrorMessage>
      )}
      {helperText && !isInvalid && (
        <FormHelperText fontSize="sm" color="brand.gray.500">
          {helperText}
        </FormHelperText>
      )}
    </FormControl>
  );
};

// Enhanced Text Input with icons and validation
interface TextInputProps {
  name: string;
  value: string;
  onChange: (value: string) => void;
  label?: string;
  placeholder?: string;
  type?: 'text' | 'email' | 'password' | 'tel' | 'url';
  icon?: React.ElementType;
  helperText?: string;
  errorMessage?: string;
  isRequired?: boolean;
  isDisabled?: boolean;
  size?: 'sm' | 'md' | 'lg';
}

export const TextInput: React.FC<TextInputProps> = ({
  name,
  value,
  onChange,
  label,
  placeholder,
  type = 'text',
  icon,
  helperText,
  errorMessage,
  isRequired = false,
  isDisabled = false,
  size = 'md',
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const isPassword = type === 'password';
  const inputType = isPassword && showPassword ? 'text' : type;

  return (
    <FormField
      label={label}
      helperText={helperText}
      errorMessage={errorMessage}
      isRequired={isRequired}
      isInvalid={!!errorMessage}
    >
      <InputGroup size={size}>
        {icon && (
          <InputLeftElement>
            <Icon as={icon} color="brand.gray.400" />
          </InputLeftElement>
        )}
        <Input
          name={name}
          type={inputType}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          isDisabled={isDisabled}
          borderColor="brand.gray.300"
          _hover={{ borderColor: 'brand.green.light' }}
          _focus={{ 
            borderColor: 'brand.green.medium',
            boxShadow: '0 0 0 1px var(--chakra-colors-brand-green-medium)'
          }}
          _invalid={{
            borderColor: 'red.400',
            _hover: { borderColor: 'red.500' },
            _focus: { borderColor: 'red.500', boxShadow: '0 0 0 1px red' }
          }}
        />
        {isPassword && (
          <InputRightElement>
            <IconButton
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              icon={<Icon as={showPassword ? FaEyeSlash : FaEye} />}
              variant="ghost"
              size="sm"
              onClick={() => setShowPassword(!showPassword)}
            />
          </InputRightElement>
        )}
      </InputGroup>
    </FormField>
  );
};

// Enhanced Textarea
interface TextAreaProps {
  name: string;
  value: string;
  onChange: (value: string) => void;
  label?: string;
  placeholder?: string;
  helperText?: string;
  errorMessage?: string;
  isRequired?: boolean;
  isDisabled?: boolean;
  rows?: number;
  maxLength?: number;
}

export const TextArea: React.FC<TextAreaProps> = ({
  name,
  value,
  onChange,
  label,
  placeholder,
  helperText,
  errorMessage,
  isRequired = false,
  isDisabled = false,
  rows = 4,
  maxLength,
}) => {
  return (
    <FormField
      label={label}
      helperText={helperText}
      errorMessage={errorMessage}
      isRequired={isRequired}
      isInvalid={!!errorMessage}
    >
      <Box position="relative">
        <Textarea
          name={name}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          isDisabled={isDisabled}
          rows={rows}
          maxLength={maxLength}
          borderColor="brand.gray.300"
          _hover={{ borderColor: 'brand.green.light' }}
          _focus={{ 
            borderColor: 'brand.green.medium',
            boxShadow: '0 0 0 1px var(--chakra-colors-brand-green-medium)'
          }}
          _invalid={{
            borderColor: 'red.400',
            _hover: { borderColor: 'red.500' },
            _focus: { borderColor: 'red.500', boxShadow: '0 0 0 1px red' }
          }}
          resize="vertical"
        />
        {maxLength && (
          <Text
            position="absolute"
            bottom="2"
            right="3"
            fontSize="xs"
            color="brand.gray.400"
          >
            {value.length}/{maxLength}
          </Text>
        )}
      </Box>
    </FormField>
  );
};

// Enhanced Select dropdown
interface SelectOption {
  value: string;
  label: string;
}

interface SelectInputProps {
  name: string;
  value: string;
  onChange: (value: string) => void;
  options: SelectOption[];
  label?: string;
  placeholder?: string;
  helperText?: string;
  errorMessage?: string;
  isRequired?: boolean;
  isDisabled?: boolean;
  size?: 'sm' | 'md' | 'lg';
}

export const SelectInput: React.FC<SelectInputProps> = ({
  name,
  value,
  onChange,
  options,
  label,
  placeholder = 'Select an option',
  helperText,
  errorMessage,
  isRequired = false,
  isDisabled = false,
  size = 'md',
}) => {
  return (
    <FormField
      label={label}
      helperText={helperText}
      errorMessage={errorMessage}
      isRequired={isRequired}
      isInvalid={!!errorMessage}
    >
      <Select
        name={name}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        isDisabled={isDisabled}
        size={size}
        borderColor="brand.gray.300"
        _hover={{ borderColor: 'brand.green.light' }}
        _focus={{ 
          borderColor: 'brand.green.medium',
          boxShadow: '0 0 0 1px var(--chakra-colors-brand-green-medium)'
        }}
        _invalid={{
          borderColor: 'red.400',
          _hover: { borderColor: 'red.500' },
          _focus: { borderColor: 'red.500', boxShadow: '0 0 0 1px red' }
        }}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </Select>
    </FormField>
  );
};

// Price Input with currency formatting
interface PriceInputProps {
  name: string;
  value: number;
  onChange: (value: number) => void;
  label?: string;
  helperText?: string;
  errorMessage?: string;
  isRequired?: boolean;
  isDisabled?: boolean;
  min?: number;
  max?: number;
  precision?: number;
}

export const PriceInput: React.FC<PriceInputProps> = ({
  name,
  value,
  onChange,
  label,
  helperText,
  errorMessage,
  isRequired = false,
  isDisabled = false,
  min = 0,
  max = 10000,
  precision = 2,
}) => {
  return (
    <FormField
      label={label}
      helperText={helperText}
      errorMessage={errorMessage}
      isRequired={isRequired}
      isInvalid={!!errorMessage}
    >
      <InputGroup>
        <InputLeftElement>
          <Icon as={FaDollarSign} color="brand.gray.400" />
        </InputLeftElement>
        <NumberInput
          name={name}
          value={value}
          onChange={(_, valueAsNumber) => onChange(isNaN(valueAsNumber) ? 0 : valueAsNumber)}
          min={min}
          max={max}
          precision={precision}
          isDisabled={isDisabled}
          w="full"
        >
          <NumberInputField
            pl="2.5rem"
            borderColor="brand.gray.300"
            _hover={{ borderColor: 'brand.green.light' }}
            _focus={{ 
              borderColor: 'brand.green.medium',
              boxShadow: '0 0 0 1px var(--chakra-colors-brand-green-medium)'
            }}
            _invalid={{
              borderColor: 'red.400',
              _hover: { borderColor: 'red.500' },
              _focus: { borderColor: 'red.500', boxShadow: '0 0 0 1px red' }
            }}
          />
          <NumberInputStepper>
            <NumberIncrementStepper />
            <NumberDecrementStepper />
          </NumberInputStepper>
        </NumberInput>
      </InputGroup>
    </FormField>
  );
};

// Price Range Slider
interface PriceRangeProps {
  name: string;
  value: [number, number];
  onChange: (value: [number, number]) => void;
  label?: string;
  helperText?: string;
  min?: number;
  max?: number;
  step?: number;
}

export const PriceRange: React.FC<PriceRangeProps> = ({
  name,
  value,
  onChange,
  label,
  helperText,
  min = 0,
  max = 1000,
  step = 10,
}) => {
  const [minVal, maxVal] = value;
  
  return (
    <FormField label={label} helperText={helperText}>
      <VStack spacing={4} align="stretch">
        <HStack justify="space-between">
          <Text fontSize="sm" color="brand.gray.600">
            ${minVal}
          </Text>
          <Text fontSize="sm" color="brand.gray.600">
            ${maxVal}
          </Text>
        </HStack>
        
        <Box px={3}>
          <Slider
            name={name}
            min={min}
            max={max}
            step={step}
            value={maxVal}
            onChange={(val) => onChange([minVal, val])}
            colorScheme="green"
          >
            <SliderTrack>
              <SliderFilledTrack />
            </SliderTrack>
            <SliderThumb boxSize={4} />
          </Slider>
        </Box>
        
        <HStack spacing={4}>
          <NumberInput
            value={minVal}
            onChange={(_, val) => onChange([isNaN(val) ? min : val, maxVal])}
            min={min}
            max={maxVal}
            size="sm"
            maxW="100px"
          >
            <NumberInputField />
          </NumberInput>
          <Text color="brand.gray.500">to</Text>
          <NumberInput
            value={maxVal}
            onChange={(_, val) => onChange([minVal, isNaN(val) ? max : val])}
            min={minVal}
            max={max}
            size="sm"
            maxW="100px"
          >
            <NumberInputField />
          </NumberInput>
        </HStack>
      </VStack>
    </FormField>
  );
};

// Submit Button with loading state
interface SubmitButtonProps {
  children: React.ReactNode;
  isLoading?: boolean;
  isDisabled?: boolean;
  loadingText?: string;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'solid' | 'outline' | 'ghost';
  colorScheme?: string;
  onClick?: () => void;
  type?: 'button' | 'submit';
  width?: string | number;
}

export const SubmitButton: React.FC<SubmitButtonProps> = ({
  children,
  isLoading = false,
  isDisabled = false,
  loadingText = 'Processing...',
  size = 'md',
  variant = 'solid',
  colorScheme = 'brand',
  onClick,
  type = 'submit',
  width = 'full',
}) => {
  return (
    <Button
      type={type}
      size={size}
      width={width}
      variant={variant}
      colorScheme={colorScheme}
      bg={variant === 'solid' ? 'brand.primary' : undefined}
      color={variant === 'solid' ? 'black' : undefined}
      _hover={variant === 'solid' ? { 
        bg: 'brand.primaryHover',
        transform: 'translateY(-2px)',
        shadow: 'lg'
      } : undefined}
      isLoading={isLoading}
      isDisabled={isDisabled}
      loadingText={loadingText}
      onClick={onClick}
    >
      {children}
    </Button>
  );
};

// Form validation hook
export const useFormValidation = () => {
  const toast = useToast();

  const validateEmail = (email: string): string | null => {
    if (!email) return 'Email is required';
    if (!/\S+@\S+\.\S+/.test(email)) return 'Email address is invalid';
    return null;
  };

  const validatePassword = (password: string): string | null => {
    if (!password) return 'Password is required';
    if (password.length < 8) return 'Password must be at least 8 characters';
    if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(password)) {
      return 'Password must contain at least one uppercase letter, one lowercase letter, and one number';
    }
    return null;
  };

  const validateRequired = (value: string, fieldName: string): string | null => {
    if (!value || value.trim() === '') return `${fieldName} is required`;
    return null;
  };

  const validatePrice = (price: number): string | null => {
    if (price < 0) return 'Price cannot be negative';
    if (price > 10000) return 'Price cannot exceed $10,000';
    return null;
  };

  const showValidationError = (message: string) => {
    toast({
      title: 'Validation Error',
      description: message,
      status: 'error',
      duration: 4000,
      isClosable: true,
    });
  };

  const showSuccess = (message: string) => {
    toast({
      title: 'Success',
      description: message,
      status: 'success',
      duration: 4000,
      isClosable: true,
    });
  };

  return {
    validateEmail,
    validatePassword,
    validateRequired,
    validatePrice,
    showValidationError,
    showSuccess,
  };
}; 